// DataLayer contract: https://github.com/runwayhealth/operations-hub/blob/main/docs/data-layer-contract.md
import posthog from "posthog-js";
import TagManager from "react-gtm-module";
import { useSelector } from "react-redux";

import {
  Currencies,
  GA4Events,
  Ga4Object,
  Item,
  ItemCategory,
  ProductEntryPoint,
} from "@types-runway/analytics";
import { Case } from "@types-runway/case";
import { Condition } from "@types-runway/condition";
import { PricesState, RootState } from "@types-runway/state";
import { currentMetaClickIdentifiers } from "@utils/analytics/metaClickIdentifiers";
import { Ga4DTO } from "@utils/analytics/Ga4DTO";
import { hashEmail } from "@utils/analytics/hashEmail";

const getPurchaseData = (items: Item[], totalAmount: number): Ga4Object => ({
  currency: "USD",
  value: totalAmount,
  addonValue: 0,
  coupon: localStorage.getItem("promoCode")?.toUpperCase() ?? null,
  items,
});

const useDataLayer = (event: GA4Events) => {
  const newCase = useSelector<RootState, Case>((state) => state.case.newCase.data);
  const prices = useSelector<RootState, PricesState>((state) => state.price);
  const userEmail = useSelector<RootState, string | undefined>((state) => state.user.data.email);
  const invoiceId = newCase.invoiceId ?? undefined;
  const initialConditions = newCase.conditions;
  const upsoldConditions = JSON.parse(sessionStorage.getItem("upsoldConditions") || "[]");

  const visitFeeItem: Item = {
    name: "Consultation",
    id: prices.case.data.id,
    price: (prices.case.data.unit_amount ?? 0) / 100,
    category: ItemCategory.SERVICE,
    productEntryPoint: ProductEntryPoint.INITIAL,
    currency: Currencies.USD,
  };

  const getProductsFromStore = (conditions: Condition[], entryPoint: ProductEntryPoint): Item[] => {
    if (!conditions || conditions.length === 0) return [];

    const formattedProducts = conditions.map((condition: Condition) => ({
      name: condition.suggestedMedication?.name ?? condition.product.name,
      id: condition.suggestedMedication?.price_id ?? condition.product.id,
      price: (condition.price ?? 0) / 100,
      category: ItemCategory.PRODUCT,
      productEntryPoint: entryPoint,
      currency: Currencies.USD,
    }));

    return formattedProducts;
  };
  const initialProducts = getProductsFromStore(initialConditions, ProductEntryPoint.INITIAL);
  const upsoldProducts = getProductsFromStore(upsoldConditions, ProductEntryPoint.ADDON);

  const products = initialProducts.reduce((mergedProductsArray, product) => {
    const matchingItem = upsoldProducts.find((addedProduct) => product.id === addedProduct.id);
    mergedProductsArray.push(matchingItem || product);
    return mergedProductsArray;
  }, [] as Item[]);

  const reportPurchasedProducts = (promocode?: string) => {
    const totalAmount = products.reduce((initAmount, product) => {
      return initAmount + product.price;
    }, 0);

    const addOnAmount = upsoldProducts.reduce((initAmount, product) => {
      return initAmount + product.price;
    }, 0);

    const visitFeePriceAmount = (prices.case.data.unit_amount ?? 0) / 100;
    const purchaseData = getPurchaseData(initialProducts, totalAmount + visitFeePriceAmount);

    const dataLayerObject = {
      ...purchaseData,
      items: [...(products ?? []), visitFeeItem],
      addonValue: upsoldConditions.length > 0 ? addOnAmount : 0,
      ...(event === GA4Events.PURCHASE && invoiceId && { transactionId: invoiceId }),
    };

    if (promocode) {
      posthog.capture("promo_code_applied", {
        promo_code: promocode,
      });
    }
    triggerEvent(new Ga4DTO(dataLayerObject));
  };

  const reportAccess = () => {
    triggerEvent(
      new Ga4DTO({
        method: "local",
      })
    );
  };

  // Single Item Events

  const singleItemEvent = (condition: Condition): Ga4Object => {
    const affectedItem: Item = {
      name: condition.suggestedMedication?.name ?? condition.product.name,
      id: condition.suggestedMedication?.price_id ?? condition.product.id,
      price: (condition.price ?? 0) / 100,
      category: ItemCategory.PRODUCT,
      productEntryPoint: ProductEntryPoint.INITIAL,
      currency: Currencies.USD,
    };

    // value and currency travel at the top level on every ecommerce event; the
    // ad-platform tags read them from there, not from the item.
    return {
      currency: Currencies.USD,
      value: affectedItem.price,
      items: [affectedItem],
    };
  };

  const reportAddToCart = (condition: Condition) => {
    triggerEvent(new Ga4DTO(singleItemEvent(condition)));
  };

  const reportRemoveFromCart = (condition: Condition) => {
    triggerEvent(new Ga4DTO(singleItemEvent(condition)));
  };

  const reportPurchaseLead = () => {
    const totalAmount = products.reduce((initAmount, product) => {
      return initAmount + product.price;
    }, 0);

    const visitFeePriceAmount = (prices.case.data.unit_amount ?? 0) / 100;
    triggerEvent(
      new Ga4DTO({
        currency: Currencies.USD,
        value: totalAmount + visitFeePriceAmount,
      })
    );
  };

  const triggerEvent = (ecommerceData: Ga4DTO) => {
    const email = userEmail?.trim();
    // PostHog gets the same properties as the data layer except the hashed email.
    const metaClickIdentifiers = currentMetaClickIdentifiers();
    TagManager.dataLayer({ dataLayer: { ecommerce: null } });
    TagManager.dataLayer({
      dataLayer: {
        event,
        ecommerce: ecommerceData,
        ...(email && { email: hashEmail(email) }),
        ...metaClickIdentifiers,
      },
    });
    posthog.capture(event, {
      ecommerce: ecommerceData,
      ...metaClickIdentifiers,
    });
  };

  return {
    reportPurchasedProducts,
    reportPurchaseLead,
    reportAccess,
    reportAddToCart,
    reportRemoveFromCart,
  };
};

export default useDataLayer;
